import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { getBuildPaymentPlan } from '../utils/buildPaymentPlan';
type Accent = 'mint' | 'amber' | 'slate';
interface BuildFeeCardProps {
  buildFee: number;
  accent?: Accent;
  showLabel?: boolean;
  /** Controlled split state. Omit to let the card manage it internally. */
  split?: boolean;
  onSplitChange?: (split: boolean) => void;
}
const ACCENTS: Record<Accent, {
  text: string;
  border: string;
  bg: string;
}> = {
  mint: {
    text: 'text-[#7FE0C3]',
    border: 'border-[#7FE0C3]/20',
    bg: 'bg-[#7FE0C3]/5'
  },
  amber: {
    text: 'text-amber-400',
    border: 'border-amber-500/20',
    bg: 'bg-amber-500/5'
  },
  slate: {
    text: 'text-slate-300',
    border: 'border-slate-700',
    bg: 'bg-slate-800/40'
  }
};
const fmt = (value: number) => value.toLocaleString('en-US');
export function BuildFeeCard({
  buildFee,
  accent = 'slate',
  showLabel = true,
  split,
  onSplitChange
}: BuildFeeCardProps) {
  const [internalPlan, setInternalPlan] = useState(false);
  const showPlan = split ?? internalPlan;
  const setShowPlan = (next: boolean) => {
    if (onSplitChange) {
      onSplitChange(next);
    } else {
      setInternalPlan(next);
    }
  };
  const styles = ACCENTS[accent];
  const plan = getBuildPaymentPlan(buildFee);
  const planToggle = <button type="button" onClick={() => setShowPlan(!showPlan)} aria-expanded={showPlan} aria-label={showPlan ? 'Hide payment plan' : 'Show payment plan'} className="uppercase tracking-widest text-inherit cursor-default focus:outline-none" data-id="element-172" data-mp="2pu3nkmeuif3c">
      {showPlan ? '(Split)' : '(One-Time)'}
    </button>;
  return <div data-id="element-173" data-mp="39h9q0rusyooi">
      {showLabel && <div className={`text-xs uppercase tracking-widest mb-2 ${styles.text}`} data-id="element-174" data-mp="39fgkjfvxeai3">
          Implementation {planToggle}
        </div>}

      <div className="flex items-baseline gap-2" data-id="element-175" data-mp="3ihg003ral6ix">
        <span className="text-4xl font-bold text-white" data-id="element-176" data-mp="3ii7xjk4g9ebu">${fmt(buildFee)}</span>
        {!showLabel && <span className={`text-sm uppercase tracking-widest ${styles.text}`} data-id="element-177" data-mp="1ifq75um6nocy">
            {planToggle}
          </span>}
      </div>

      <AnimatePresence initial={false} data-id="element-178" data-mp="1s500tewodf36">
        {showPlan && <motion.div initial={{
        opacity: 0,
        height: 0
      }} animate={{
        opacity: 1,
        height: 'auto'
      }} exit={{
        opacity: 0,
        height: 0
      }} transition={{
        duration: 0.2
      }} className="overflow-hidden" data-id="element-179" data-mp="1s6b2982near4">
            <div className={`mt-4 rounded-lg border px-4 py-3 ${styles.border} ${styles.bg}`} data-id="element-180" data-mp="1x4w399pq4ygm">
              <div className={`text-[10px] font-bold uppercase tracking-widest mb-2 ${styles.text}`} data-id="element-181" data-mp="2b3c9kzjocmy8">
                Split over the first 3 months
              </div>
              <ul className="space-y-1.5 text-sm" data-id="element-182" data-mp="gqd07o5w0oze">
                <li className="flex justify-between" data-id="element-183" data-mp="br040ct4m5k5">
                  <span className="text-slate-400" data-id="element-184" data-mp="bskxdz3pfigf">Due at signing</span>
                  <span className="text-white font-medium" data-id="element-185" data-mp="2nf4wjpqvbgs3">
                    ${fmt(plan.down)}
                  </span>
                </li>
                <li className="flex justify-between" data-id="element-186" data-mp="2sfaqjckm1fdc">
                  <span className="text-slate-400" data-id="element-187" data-mp="2ekql5y9gkqe9">Month 2</span>
                  <span className="text-white font-medium" data-id="element-188" data-mp="2indczz64qbbg">
                    ${fmt(plan.monthly)}
                  </span>
                </li>
                <li className="flex justify-between" data-id="element-189" data-mp="24mud5fbtibkd">
                  <span className="text-slate-400" data-id="element-190" data-mp="29mfaxmf94dvi">Month 3</span>
                  <span className="text-white font-medium" data-id="element-191" data-mp="29o01z22cxeom">
                    ${fmt(plan.monthly)}
                  </span>
                </li>
              </ul>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}